import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

import Box from '../Box'
import Icon from '../Icon'
import Overlay from '../Overlay'
import colors from '../../constants/colors'

import { StyledDrawer } from './styled'

import { DrawerComposition, DrawerHeaderProps, DrawerProps } from './Drawer.types'
import Flex from '../Flex'
import Text from '../Text'

const Drawer: React.FC<DrawerProps> & DrawerComposition = ({
  children,
  isVisible,
  onClose,
  header,
  placement = 'left',
  closable = false,
  isFullPage = false,
  isNestedDrawer,
  ...restProps
}) => {
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    const bodyStyle = document.body.style

    if (isVisible) {
      if (!isNestedDrawer) bodyStyle.setProperty('overflow-y', 'hidden')
      return setIsActive(true)
    }

    setTimeout(() => {
      if (!isNestedDrawer) bodyStyle.removeProperty('overflow-y')
      setIsActive(false)
    }, 300)
  }, [isVisible])

  if (!isVisible && !isActive) return null

  const className = 'renos-drawer'

  const zIndex = restProps.zIndex || 10

  const onCloseOverlay = () => {
    if (!closable) return
    if (onClose) return onClose()
    if (typeof header?.closeButton === 'object') header.closeButton.onClose?.()
  }

  return createPortal(
    <Box>
      <StyledDrawer
        position='fixed'
        background={colors.white}
        overflow='auto'
        zIndex={zIndex}
        placement={placement}
        className={className}
        isFullPage={isFullPage}
        aria-hidden={!isVisible}
        isOpen={isVisible && isActive}
        padding='16px'
        {...restProps}
      >
        <Header {...header} onClose={onClose} closable={closable} />

        {children}
      </StyledDrawer>
      <Box onClick={onCloseOverlay}>
        <Overlay zIndex={Number(zIndex) - 1} />
      </Box>
    </Box>,
    document.body,
  )
}

const Header: React.FC<DrawerHeaderProps> = ({
  closeButton: closeButtonHeader,
  title,
  suffix,
  render,
  onClose,
  closable,
  ...rest
}) => {
  const withCloseButton = closeButtonHeader !== false
  const closeButton = typeof closeButtonHeader === 'object' && closeButtonHeader

  const CloseButtonIcon = closeButton.render || (
    <Icon name={closeButton.icon || 'Close-outline'} size={24} color='black50' />
  )

  if (React.isValidElement(render)) return render

  return (
    <Flex gap='8px' alignItems='center' {...rest}>
      {closable && withCloseButton && (
        <Box onClick={closeButton.onClose || onClose} cursor='pointer' aria-label='close'>
          {CloseButtonIcon}
        </Box>
      )}

      {title && (
        <Box flex={1}>
          <Text color='black100' weight='bold' variant='heading3' lineClamp={1}>
            {title}
          </Text>
        </Box>
      )}

      {suffix}
    </Flex>
  )
}

Drawer.Header = Header

export default Drawer

export type { DrawerProps }
